import { useEffect, useRef, useState } from "react";
import type { LottieRefCurrentProps } from "lottie-react";
import Lottie from "lottie-react";
import { t } from "@/i18n-js/instance";
import type { AgentProp } from "@circle-react/components/SettingsApp/CommunityBot/Agents";
import { useProgress } from "@circle-react/pages/BotBuilder/contexts/ProgressContext";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { HeaderButtons } from "./HeaderButtons";
import spinnerAnimation from "./KnowledgePage/animations/spinner-header.json";
import { LeftBackArrow } from "./LeftBackArrow";
import { TitleInput } from "./TitleInput";

interface HeaderProps {
  agent: AgentProp;
  onClose: () => void;
}

export const Header = ({ agent, onClose }: HeaderProps) => {
  const { totalProgress, isLoading } = useProgress();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const hasStartedLoadingRef = useRef(false);

  const [wasEverInProgress, setWasEverInProgress] = useState(false);
  const [isProgressInfoVisible, setIsProgressInfoVisible] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setWasEverInProgress(true);
      setIsProgressInfoVisible(true);
      setShouldCollapse(false);
      return undefined;
    }

    if (wasEverInProgress && totalProgress === 100) {
      // Hide progress info immediately
      setIsProgressInfoVisible(false);
      // When transitioning to done, play the final animation then collapse
      const timer = setTimeout(() => {
        setShouldCollapse(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoading, wasEverInProgress, totalProgress]);

  // Handle Lottie animation segments based on status changes
  useEffect(() => {
    if (!lottieRef.current?.animationItem) return;

    const lottie = lottieRef.current.animationItem;

    if (shouldCollapse) {
      lottie.pause();
      return;
    }

    if (isLoading && !hasStartedLoadingRef.current) {
      hasStartedLoadingRef.current = true;
      // Initial appearance animation
      lottie.loop = false;
      lottie.playSegments([0, 42], true);
    } else if (isLoading) {
      // Loading loop animation
      lottie.setSegment(42, 119);
      lottie.loop = true;
      lottie.play();
    } else if (!isLoading && totalProgress === 100 && wasEverInProgress) {
      // Final completion animation
      lottie.loop = false;
      lottie.playSegments([119, 172], true);
    }
  }, [isLoading, wasEverInProgress, totalProgress, shouldCollapse]);

  if (!wasEverInProgress && !isLoading) {
    return (
      <div className="text-dark border-primary bg-primary sticky flex w-full items-center justify-between border-b px-5 py-4">
        <div className="bg-primary flex h-full w-full justify-between">
          <div className="flex w-full flex-row items-center gap-6 truncate">
            <LeftBackArrow onClick={onClose} />
            <TitleInput />
          </div>
          <div className="flex w-full items-center justify-end gap-3">
            <HeaderButtons agent={agent} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-dark border-primary bg-primary sticky flex w-full items-center justify-between border-b px-5 py-4">
      <div className="bg-primary flex h-full w-full justify-between">
        <div className="flex w-full flex-row items-center gap-6 truncate">
          <LeftBackArrow onClick={onClose} />
          <TitleInput />
        </div>
        <div className="flex w-full items-center justify-end gap-3">
          <div className="flex items-center gap-2">
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${isProgressInfoVisible && !shouldCollapse ? "max-h-6 opacity-100" : "max-h-0 opacity-0"}`}
            >
              <Typography.LabelSm color="text-light">
                {t(
                  "community_bot.bot_builder.knowledge_page.content_loading_message",
                  {
                    progress: totalProgress,
                  },
                )}
              </Typography.LabelSm>
            </div>
            <div
              className={`transition-all duration-500 ease-in-out ${shouldCollapse ? "w-0 opacity-0" : "w-6 opacity-100"}`}
            >
              <Lottie
                lottieRef={lottieRef}
                animationData={spinnerAnimation}
                autoplay={false}
                loop={false}
              />
            </div>
          </div>
          <HeaderButtons agent={agent} />
        </div>
      </div>
    </div>
  );
};
